import React, { Component } from 'react';
import { connect } from 'react-redux';
import DOMPurify from 'dompurify';
import { capitalize, get, isEmpty, groupBy, find, forOwn, isEqual } from 'lodash';
import draftToHtml from 'draftjs-to-html';
import packageJson from '../../../package.json';
import { fetchRestaurantDataAction } from '../../actions/app.action';
import { constants } from '../../constants/configuration';
import { exceptionComponentPageTypes, restaurantDataDepthParams } from '../../constants/globals';
import {
  extractWhitelabelSubdomain,
  sortObjectByKeysUsingComparator,
  toTitleCase,
} from '../../utils/helpers';
import { formatTimeTo12H } from '../../utils/fudrLocalizationHelper';
import routes from '../../routes';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import phoneIcon from '../../assets/images/icons/phone.svg';
import sideFudrIcon from '../../assets/images/icons/side-fudr.svg';
import directionIcon from '../../assets/images/icons/direction.svg';
import './AboutOutlet.scss';

const Header = asyncRoute(() => import('../../components/Header'));
const ContactInfoPopup = asyncRoute(() => import('../../components/Popup/ContactInfo'));
const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));

const aboutOutletConfig = constants.aboutOutlet;

class AboutOutlet extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showContactInfoPopup: false,
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      handleErrorCTA: null,
      errorPopupStyleType: exceptionComponentPageTypes.halfSize,
      depthParam: [
        restaurantDataDepthParams.config,
        restaurantDataDepthParams.workhours,
        restaurantDataDepthParams.serviceTypes,
        restaurantDataDepthParams.notificationEmails,
        restaurantDataDepthParams.phoneNumbers,
      ],
    };
  }

  componentDidMount() {
    const { match, appStartupInfo } = this.props;
    let restaurantId = get(match, 'params.restaurantId', '');

    // Bring restaurant id from whitelabel info incase of valid whitelabel subdoamin URL hit, otherwise from the restaurantData
    const subDomain = extractWhitelabelSubdomain();

    restaurantId = subDomain ? get(appStartupInfo, 'source', '') : restaurantId;
    if (!isEmpty(restaurantId)) {
      const { depthParam } = this.state;
      // fetch restaurnat data if restaurantId is not empty
      this.props.fetchRestaurantDataAction({ restaurantId, depthParam });
    }

    // Scroll to top to prevent browser scroll position persistence from other screens
    window.scrollTo({ top: 0 });
  }

  componentWillReceiveProps(nextProps) {
    if (
      !isEqual(get(this.props, 'appStartupInfo'), get(nextProps, 'appStartupInfo')) &&
      !isEmpty(get(nextProps, 'appStartupInfo'))
    ) {
      const { source: restaurantId } = get(nextProps, 'appStartupInfo', {});
      if (!isEmpty(restaurantId)) {
        const { depthParam } = this.state;
        // fetch restaurnat data if restaurantId is not empty
        this.props.fetchRestaurantDataAction({ restaurantId, depthParam });
      }
    }

    if (
      !isEqual(get(this.props, 'restaurantData'), get(nextProps, 'restaurantData')) &&
      !isEmpty(get(nextProps, 'restaurantData'))
    ) {
      this.setState({
        showErrorPopup: false,
        errorContent: {
          title: '',
          description: '',
          buttonCta: '',
        },
        handleErrorCTA: null,
        errorPopupStyleType: exceptionComponentPageTypes.halfSize,
      });
    }
  }

  onBackClick = () => {
    const { history, selectedTable, restaurantData } = this.props;
    // redirect to welcome screen if selected table is empty
    if (isEmpty(selectedTable)) {
      const restaurantCode = get(restaurantData, 'restaurantCode', '');
      history.push(`/${routes.containers.welcome}/${restaurantCode}`);
      return;
    }
    history.goBack();
  };

  onAboutFudrClick = () => {
    this.props.history.push(`/${routes.containers.aboutFudr}`);
  };

  handlePhoneIconClick = () => {
    this.setState({
      showContactInfoPopup: true,
    });
  };

  renderPhoneSection = (restaurantContactNumbers) => {
    if (restaurantContactNumbers && restaurantContactNumbers.length === 1) {
      const contactNumber = get(restaurantContactNumbers, '0.phoneNumber', '');
      let href = `tel:${contactNumber}`;
      return (
        <div className="contact-number">
          <div>{contactNumber}</div>
          <div>
            <a href={href}>
              <img src={phoneIcon} alt="phone" />
            </a>
          </div>
        </div>
      );
    } else {
      return (
        <div className="contact-number">
          <div>
            {restaurantContactNumbers.map(
              (contactNumber, index) =>
                `${get(contactNumber, 'code', '')} ${get(contactNumber, 'phoneNumber', '')}${
                  index < restaurantContactNumbers.length - 1 ? ', ' : ''
                } `,
            )}
          </div>
          <div>
            <img src={phoneIcon} alt="phone" onClick={this.handlePhoneIconClick} />
          </div>
        </div>
      );
    }
  };

  onCloseContactInfoPopup = () => {
    this.setState({
      showContactInfoPopup: false,
    });
  };

  handleContextlessURLHitErrorPopupCTA = () => {
    // Reload and open webapp's home
    window.location.replace(window.location.origin);
  };

  handleGetDirection = () => {
    const { restaurantConfig } = this.props.restaurantData;
    const directionLink = get(restaurantConfig, 'directionLink', '');
    window.open(directionLink, '_blank');
  };

  // Compare function takes values being compared as parameters from sort() function and returns a comparator (integer) - negative implying first comes before, zero implying same and positive implying after, post sorting.
  compareFunction = (firstValue, secondValue) => {
    const { weekdays } = this.props.masterData;
    const comparator =
      get(
        find(weekdays, (day) => get(day, 'code', '') === firstValue),
        'ordinal',
        1,
      ) -
      get(
        find(weekdays, (day) => get(day, 'code', '') === secondValue),
        'ordinal',
        1,
      );
    return comparator;
  };

  getRemainingWorkhourTypeName = ({ serviceTypes, groupedWorkhours, masterServiceType }) => {
    const availableServiceTypes =
      serviceTypes && serviceTypes.length > 0 && serviceTypes.filter((sType) => !sType.isStopped); // filter the available serviceTypes based on the isStopped flag
    let name = '';
    const keysArr = Object.keys(groupedWorkhours);
    let remainingServiceTypes = !isEmpty(availableServiceTypes)
      ? availableServiceTypes.filter((sType) => !keysArr.includes(sType.code))
      : [];
    name =
      remainingServiceTypes &&
      remainingServiceTypes.length > 0 &&
      remainingServiceTypes.map(
        (sType, index) =>
          `${
            !isEmpty(get(sType, 'customName', ''))
              ? toTitleCase(get(sType, 'customName', ''))
              : get(
                  masterServiceType.find((mServiceType) => mServiceType.code === sType.code),
                  'title',
                  '',
                )
          }${index < remainingServiceTypes.length - 1 ? ', ' : ''}`,
      );
    return name;
  };

  getWorkhourTypeName = ({ key, groupedWorkhours, serviceTypes }) => {
    const { serviceTypes: masterServiceType } = this.props.masterData;
    const serviceTypeObj = serviceTypes.find((sType) => sType.code === key);
    const masterServiceTypeObj = masterServiceType.find((sType) => sType.code === key);
    let name = !isEmpty(get(serviceTypeObj, 'customName', ''))
      ? toTitleCase(get(serviceTypeObj, 'customName', ''))
      : get(
          masterServiceTypeObj,
          'title',
          this.getRemainingWorkhourTypeName({
            serviceTypes,
            groupedWorkhours,
            masterServiceType,
          }),
        );
    return name;
  };

  renderOutletTimings = ({ workhours, serviceTypes }) => {
    let outletTimings = [];
    // get available workhours array from the workhours array based on the available serviceTypes
    const availableWorkhours =
      workhours &&
      workhours.length > 0 &&
      workhours.filter((workhour) =>
        !isEmpty(serviceTypes)
          ? serviceTypes.find(
              (sType) =>
                (!sType.isStopped && sType.code === workhour.serviceType) || !workhour.serviceType,
            ) || {}
          : {},
      );
    let groupedWorkhours = groupBy(availableWorkhours, 'serviceType');
    forOwn(groupedWorkhours, (value, key) => {
      let restaurantWorkhour = [];
      restaurantWorkhour = groupBy(value, 'day'); // Restructure restaurant workhours grouping them based on 'day' key
      restaurantWorkhour = sortObjectByKeysUsingComparator(
        restaurantWorkhour,
        this.compareFunction,
      );
      restaurantWorkhour = Object.values(restaurantWorkhour);
      outletTimings.push(
        <div className="about-outlet_workhour-type">
          {this.getWorkhourTypeName({ key, groupedWorkhours, serviceTypes })}
        </div>,
      );
      restaurantWorkhour.map((workhour) =>
        outletTimings.push(
          <div className="about-outlet_weekday">
            <span className="day">{capitalize(get(...workhour, 'day', ''))}</span>
            <span>
              {!get(...workhour, 'isClosed', false)
                ? `${formatTimeTo12H(get(...workhour, 'startTime', ''))} - ${formatTimeTo12H(
                    get(...workhour, 'endTime', ''),
                  )}`
                : aboutOutletConfig.closed}
            </span>
          </div>,
        ),
      );
    });
    return outletTimings;
  };

  renderCustomerSupportEmail = ({ restaurantNotificationEmail }) => {
    let email = '';
    const restaurantDefaultSupportEmail = // get restaurant default support email
      restaurantNotificationEmail &&
      restaurantNotificationEmail.length > 0 &&
      get(
        restaurantNotificationEmail.find(
          (notificationEmail) => notificationEmail.type === 'DEFAULT',
        ),
        'restaurantEmailAddressList',
        [],
      );
    const restaurantCustomerSupportEmail = // get restaurant customer support email
      restaurantNotificationEmail &&
      restaurantNotificationEmail.length > 0 &&
      get(
        restaurantNotificationEmail.find(
          (notificationEmail) => notificationEmail.type === 'CUSTOMER_SUPPORT',
        ),
        'restaurantEmailAddressList',
        [],
      );
    email =
      restaurantCustomerSupportEmail && restaurantCustomerSupportEmail.length > 0
        ? restaurantCustomerSupportEmail.map((customerSupportEmail, index) => (
            <a
              key={get(customerSupportEmail, 'id', index)}
              href={`mailto:${get(customerSupportEmail, 'email', '')}`}
            >
              {`${get(customerSupportEmail, 'email', '')}${
                index < restaurantCustomerSupportEmail.length - 1 ? ', ' : ''
              }`}
            </a>
          ))
        : restaurantDefaultSupportEmail &&
          restaurantDefaultSupportEmail.length > 0 &&
          restaurantDefaultSupportEmail.map((defaultSupportEmail, index) => (
            <a
              key={get(defaultSupportEmail, 'id', index)}
              href={`mailto:${get(defaultSupportEmail, 'email', '')}`}
            >
              {`${get(defaultSupportEmail, 'email', '')}${
                index < restaurantDefaultSupportEmail.length - 1 ? ', ' : ''
              }`}
            </a>
          ));
    return email;
  };

  render() {
    const {
      showContactInfoPopup,
      showErrorPopup,
      errorContent,
      handleErrorCTA,
      errorPopupStyleType,
    } = this.state;
    const {
      restaurantPhoneNumbers,
      address,
      workhours,
      restaurantConfig,
      serviceTypes,
      restaurantNotificationEmail,
    } = this.props.restaurantData;
    const aboutOutlet = DOMPurify.sanitize(
      !isEmpty(get(restaurantConfig, 'about', ''))
        ? draftToHtml(JSON.parse(restaurantConfig.about))
        : '',
    );
    const restaurantContactNumbers =
      restaurantPhoneNumbers &&
      restaurantPhoneNumbers.length > 0 &&
      restaurantPhoneNumbers.filter(
        (phoneNumber) => get(phoneNumber, 'isVisibleOnMenu', false) === true,
      );
    return (
      <>
        <Header onBackClick={this.onBackClick} identifier={routes.containers.aboutOutlet} />
        <div className="about-outlet" id="about-outlet-wrapper">
          <div className="about-outlet_title">{aboutOutletConfig.title}</div>
          {aboutOutlet && (
            <div
              className="about-outlet_description"
              dangerouslySetInnerHTML={{ __html: aboutOutlet }}
            />
          )}
          <div className="about-outlet_contact-us">
            <div>{aboutOutletConfig.forAnyQueriesText}</div>
            <div className="phone-section">
              <div>{aboutOutletConfig.phone}</div>
              {restaurantContactNumbers && restaurantContactNumbers.length > 0
                ? this.renderPhoneSection(restaurantContactNumbers)
                : aboutOutletConfig.notProvidedText}
            </div>
            <div className="enmail-section">
              <div>{aboutOutletConfig.email}</div>
              <div>{this.renderCustomerSupportEmail({ restaurantNotificationEmail })}</div>
            </div>
            <div className="address-section">
              <div>{aboutOutletConfig.address}</div>
              <div className="restaurant-address">
                <span>{address ? address : '--'}</span>
                <div>
                  {!isEmpty(get(restaurantConfig, 'directionLink', '')) && (
                    <span>
                      <img src={directionIcon} alt="direction" onClick={this.handleGetDirection} />
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <span className="about-outlet_line-break" />
          <div className="about-outlet_timings">
            <div className="about-outlet_title">{aboutOutletConfig.outletTimings}</div>
            <div className="restaurant-workhours">
              {this.renderOutletTimings({ workhours, serviceTypes })}
            </div>
          </div>
          <span className="about-outlet_line-break" />
          <div className="about-outlet_about-fudr">
            <div className="text">{aboutOutletConfig.poweredBy}</div>
            <div className="links" onClick={this.onAboutFudrClick}>
              <div>
                <img src={sideFudrIcon} alt="FUDR" />
              </div>
              <i className="chevron right icon"></i>
            </div>
          </div>
          <div className="about-outlet_app-version">{`v ${packageJson.version}`}</div>
        </div>
        {showContactInfoPopup && (
          <ContactInfoPopup
            openSegmentPopup={showContactInfoPopup}
            dismissSegmentPopup={this.onCloseContactInfoPopup}
            restaurantContactNumbers={restaurantContactNumbers}
          />
        )}
        {showErrorPopup && (
          <ExceptionComponent
            pageType={errorPopupStyleType}
            openSegmentPopup={showErrorPopup}
            title={get(errorContent, 'title')}
            description={get(errorContent, 'description')}
            buttonCta={get(errorContent, 'buttonCta')}
            handleButtonCta={handleErrorCTA}
          />
        )}
      </>
    );
  }
}

export default connect(
  (state) => ({
    restaurantData: get(state, 'app.restaurant', {}),
    masterData: get(state, 'app.masterData', {}),
    appStartupInfo: get(state, 'common.appStartupInfo.subdomainObj', {}),
    selectedTable: get(state, 'app.selectedTable', {}),
  }),
  { fetchRestaurantDataAction },
)(AboutOutlet);
